// @preval
// Copyright (c) 2015-present, Salesforce, Inc. All rights reserved
// Licensed under BSD 3-Clause - see LICENSE.txt or git.io/sfdc-license

const glob = require("glob");
const Immutable = require("immutable");
const _ = require("lodash");
const path = require("path");
const fs = require("fs");
const { kebabCase } = require("theo/lib/util");
const findNodeModules = require("../helpers/findNodeModules");
const nodeModulesDir = findNodeModules(__dirname);

const sldsModule = "@salesforce-ux/design-system";

const categoryBase = Immutable.fromJS({
  /**
   * Return true if an icon should be included in a category
   *
   * @param {object} icon
   * @returns {boolean}
   */
  filter(icon) {
    return true;
  },
  /**
   * Return the corresponding prop name from the design properties
   *
   * @param {object} icon
   * @returns {string}
   */
  getPropName(icon) {
    return _.camelCase(icon.get("symbol"));
  },
  /**
   * Return the className for a given icon
   *
   * @param {string} spriteName
   * @param {string} symbolName
   * @returns {string}
   */
  getClassName(spriteName, symbolName) {
    return `slds-icon-${symbolName}`;
  },
  /**
   * Description displayed on the page
   */
  description: "",
  /**
   * An optional object mapping of design tokens / values
   */
  props: false,
});

const categories = Immutable.Map({
  utility: categoryBase.merge(
    Immutable.fromJS({
      getClassName(spriteName, symbolName) {
        // Utility icons have no background, so we're not generating any class for it
        return null;
      },
      description:
        "Utility icons are used throughout the interface and are SVG's for extensibility.",
    })
  ),

  doctype: categoryBase.merge(
    Immutable.fromJS({
      getClassName(spriteName, symbolName) {
        // Doctype icons have no background, so we're not generating any class for it
        return null;
      },
      description:
        "Doctype icons represent a type of file when a preview or image is unavailable",
    })
  ),

  standard: categoryBase.merge(
    Immutable.fromJS({
      getClassName(spriteName, symbolName) {
        return `slds-icon-${spriteName}-${symbolName}`;
      },
      description:
        "Standard icons represent entities and objects within Salesforce.",
      props: require(`${sldsModule}/design-tokens/dist/bg-standard.common.js`),
    })
  ),

  custom: categoryBase.merge(
    Immutable.fromJS({
      getClassName(spriteName, symbolName) {
        // The desired format is: `icon-custom-custom27`, following the icon names displayed on the page
        // below each icon, and be consistent with the class names in other sets
        return `slds-icon-${spriteName}-${symbolName.replace(
          /[^a-z0-9]/g,
          ""
        )}`;
      },
      description:
        "Custom icons are available for the identity of user created objects.",
      props: require(`${sldsModule}/design-tokens/dist/bg-custom.common.js`),
    })
  ),

  action: categoryBase.merge(
    Immutable.fromJS({
      filter(icon) {
        return !/custom/.test(icon.get("symbol"));
      },
      getPropName(icon) {
        // Currently, actions look like: "actionSomeAction"
        return _.camelCase("action" + _.capitalize(icon.get("symbol")));
      },
      getClassName(spriteName, symbolName) {
        return `slds-icon-${spriteName}-${symbolName}`;
      },
      description:
        "Actions can be seen throughout the interface and represent actions a user can take on any given screen.",
      props: require(`${sldsModule}/design-tokens/dist/bg-actions.common.js`),
    })
  ),
});

const toIcon = (svg) => {
  const spriteName = path.basename(path.dirname(svg));
  const category = categories.get(spriteName, categoryBase);
  const symbolName = path.basename(svg, ".svg");
  const className = category.get("getClassName")(
    spriteName,
    kebabCase(symbolName.toLowerCase())
  );

  const iconSets = ["action", "custom", "doctype", "standard", "utility"];
  const iconSynonyms = { icon_synonyms: {} };

  iconSets.forEach((iconSet) => {
    let iconFile = `${nodeModulesDir}/@salesforce-ux/design-system/metadata/icons/${iconSet}-icons-metadata.json`;
    iconSynonyms.icon_synonyms[iconSet] = JSON.parse(
      fs.readFileSync(iconFile, "utf-8")
    );
  });

  try {
    var synonyms = iconSynonyms.icon_synonyms[spriteName][symbolName].synonyms;
    synonyms.push(symbolName);
  } catch {
    var synonyms = [symbolName];
  }

  let icon = Immutable.Map({
    sprite: spriteName,
    symbol: symbolName,
    synonyms: synonyms,
    visible: true,
    className,
  });
  // If a category has design props, add a class to symbols
  // that don't have a corresponding value / generated className
  if (category.has("props")) {
    const name = category.get("getPropName")(icon);
    if (!category.getIn(["props", name])) {
      icon = icon.update(
        "className",
        (className) => className + " slds-icon__svg--default"
      );
    }
  }
  return icon;
};

const toIcons = (svgs) => {
  let sprites = categories.keySeq().reduce((sprites, name) => {
    const category = categories.get(name);
    const category_ = Immutable.fromJS({
      name,
      description: category.get("description"),
      icons: [],
    });
    return sprites.set(name, category_);
  }, Immutable.OrderedMap());
  return svgs.reduce((sprites, svg) => {
    const icon = toIcon(svg);
    const spriteName = icon.get("sprite");
    const category = categories.get(spriteName, categoryBase);
    return category.get("filter")(icon)
      ? sprites.updateIn([spriteName, "icons"], (icons) => icons.push(icon))
      : sprites;
  }, sprites);
};

const generate = () => {
  const svgs = Immutable.List(
    glob.sync(`${nodeModulesDir}/${sldsModule}/assets/icons/**/*.svg`)
  ).filter((p) => !/-sprite/.test(p));
  return (
    toIcons(svgs)
      // Natural sorting for only the custom section of icons
      .updateIn(["custom", "icons"], (icons) =>
        icons.sort((a, b) => {
          a = parseInt(a.get("symbol").replace(/^[^\d]*/, ""), 10);
          b = parseInt(b.get("symbol").replace(/^[^\d]*/, ""), 10);
          return a > b ? 1 : -1;
        })
      )
      .toList()
  );
};

module.exports = generate();
